import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
// Async
import PropertiesAsync from 'store/properties/propertiesAsync';
import DestinationTypesAsync from 'store/destinationTypes/destinationTypesAsync';
// Actions
import { UiActions } from 'store/ui/uiSlice';
// Selectors
import { selectDestinationTypes } from 'store/destinationTypes/destinationTypesSelectors';
// Hooks
import { useAppDispatch } from 'hooks/redux';
// Mui
import {
  Button, DialogActions, DialogContent,
  DialogTitle, TextField, MenuItem, InputLabel,
  FormControl, OutlinedInput, Box, Chip, Select,
  FormHelperText
} from '@mui/material';
import {
  LoadingButton
} from '@mui/lab';
// Utilities
import { isFieldRequired } from 'utilities/Validation';
import { getContent } from 'utilities/Utilities';

interface IFormData {
  name: string,
  description: string,
  destinationTypesIds: number[],
  country: string,
  region: string,
}

type Props = {
  onClose: () => void;
}

const PropertiesFormDialog:React.FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch();

  // State
  const destinationTypes = useSelector(selectDestinationTypes) || [];
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { control, handleSubmit, formState:{ errors } } = useForm<IFormData>({
    defaultValues: {
      name: '',
      description: '',
      destinationTypesIds: [],
      country: '',
      region: '',
    }
  });

  const onSubmit = handleSubmit((data:IFormData) => {
    setIsLoading(true);
    dispatch(PropertiesAsync.createProperty(data))
      .unwrap()
      .then(() => {
        dispatch(UiActions.enqueueSnackbar({ message: 'Property was created' }));
      })
      .then(() => onClose())
      .finally(() => setIsLoading(false));
  });

  useEffect(() => {
    dispatch(DestinationTypesAsync.fetchDestinationTypes({}));
    // eslint-disable-next-line
  }, []);

  return (
    <React.Fragment>
      <DialogTitle>Create property</DialogTitle>
      <DialogContent dividers>
        <form onSubmit={onSubmit} noValidate>
          {/* Name */}
          <Controller
            control={control} name="name"
            rules={{ required: isFieldRequired }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin='normal'
                label="Name"
                type="text"
                error={Boolean(errors.name)}
                helperText={errors.name ? errors.name.message : ''}
                required
              />
            )}
          />

          
          {/* Destination Type */}
          <Controller
            control={control} name="destinationTypesIds"
            rules={{ required: isFieldRequired }}
            render={({ field }) => (
              <FormControl fullWidth margin="normal">
                <InputLabel id="destinationType-multiple-chip-label">{`${getContent('labels').labelDestinationTypeSingularText}(s) *`}</InputLabel>
                <Select
                  {...field}
                  labelId="destinationType-multiple-chip-label"
                  id="destinationType-multiple-chip"
                  multiple
                  required
                  error={Boolean(errors.destinationTypesIds)}
                  input={<OutlinedInput id="destinationType-multiple-chip" label="Destination type(s) *" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value:number) => (
                        <Chip key={value} label={destinationTypes.find(destinationType => destinationType.value === value)?.label} sx={{ height: '23px' }} />
                      ))}
                    </Box>
                  )}
                >
                  {destinationTypes.map((destinationType) => (
                    <MenuItem
                      key={destinationType.value}
                      value={destinationType.value}
                    >
                      {destinationType.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ color: '#d32f2f' }}>{errors.destinationTypesIds ? (errors.destinationTypesIds as any).message : ''}</FormHelperText>
              </FormControl>
            )}
          />

          {/* Region */}
          <Controller
            control={control} name="region"
            rules={{ required: isFieldRequired }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin='normal'
                label="Region"
                type="text"
                error={Boolean(errors.region)}
                helperText={errors.region ? errors.region.message : ''}
                required
              />
            )}
          />

          {/* Country */}
          <Controller
            control={control} name="country"
            rules={{ required: isFieldRequired }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin='normal'
                label="Country"
                type="text"
                error={Boolean(errors.country)}
                helperText={errors.country ? errors.country.message : ''}
                required
              />
            )}
          />

          {/* Description */}
          <Controller
            control={control} name="description"
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin='normal'
                label="Description"
                type="text"
                multiline
                rows={5}
              />
            )}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          color="primary"
          onClick={onClose}
        >Cancel</Button>
        <LoadingButton
          type="submit"
          loading={isLoading}
          onClick={onSubmit}
          variant="contained"
          color="primary"
        >
          Create
        </LoadingButton>
      </DialogActions>
    </React.Fragment>
  )
}

export default PropertiesFormDialog;
